import React, { useState, useEffect } from 'react';
import { ChartBar, Users, FileText, Briefcase } from '@phosphor-icons/react';
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import AuthModal from "../components/AuthModal";
import { auth } from '../lib/firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import SEO from "../seo/SEO";

const LandingPage = () => {
    const [showAuth, setShowAuth] = useState(false);
    const [authMode, setAuthMode] = useState('login');
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Listen for authentication state changes
        const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
            setUser(currentUser);
            setLoading(false);
        });

        // Cleanup subscription on unmount
        return () => unsubscribe();
    }, []);

    const openAuth = (mode) => {
        setAuthMode(mode);
        setShowAuth(true);
    };

    const handleSignOut = async () => {
        try {
            await signOut(auth);
            // User will be automatically updated via onAuthStateChanged
        } catch (error) {
            console.error('Error signing out:', error);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[#F9F9F6] flex items-center justify-center">
                <div className="text-center">
                    <div className="w-12 h-12 border-4 border-[#002FA7] border-t-transparent rounded-full animate-spin mx-auto"></div>
                    <p className="mt-4 text-[#666666]">Loading...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#F9F9F6]">
            <SEO title="Home | MyProfessionalPath"
                 description="myprofessionalpath is an online professional development system. The system is aligned with
                 the Engineering Council of South Africa for candidate engineers, technologists and technicians.  Progress
                 can be measured and tracked against the competence statements for the chosen registration category.
                 It can also be used for individual company CPD reporting.. The system is mobile and tablet compatible so
                 records can be accessed or added to on-the-go."
                 canonicalURL="https://myprofessionalpath.web.app" />
            {/* Navigation */}
            <nav className="sticky-nav" data-testid="landing-nav">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <div className="flex items-center space-x-3">
                            <ChartBar size={32} weight="duotone" className="text-[#002FA7]" />
                            <span className="font-heading text-xl font-semibold text-[#0A0A0A]">MyProfessionalPath</span>
                        </div>
                        <div className="flex items-center space-x-4">
                            {user ? (
                                <>
                                    <span className="text-sm text-[#666666] hidden sm:inline-block">
                                        Welcome, {user.displayName || user.email?.split('@')[0]}
                                    </span>
                                    <Button
                                        variant="ghost"
                                        onClick={handleSignOut}
                                        data-testid="nav-signout-btn"
                                        className="text-sm font-medium"
                                    >
                                        Sign Out
                                    </Button>
                                    <Button
                                        onClick={() => window.location.href = '/dashboard'}
                                        data-testid="nav-dashboard-btn"
                                        className="bg-[#002FA7] hover:bg-[#0040D9] text-white rounded-sm text-sm font-medium"
                                    >
                                        Dashboard
                                    </Button>
                                </>
                            ) : (
                                <>
                                    <Button
                                        variant="ghost"
                                        onClick={() => openAuth('login')}
                                        data-testid="nav-login-btn"
                                        className="text-sm font-medium"
                                    >
                                        Sign In
                                    </Button>
                                    <Button
                                        onClick={() => openAuth('register')}
                                        data-testid="nav-register-btn"
                                        className="bg-[#002FA7] hover:bg-[#0040D9] text-white rounded-sm text-sm font-medium"
                                    >
                                        Get Started
                                    </Button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="hero-section py-20 sm:py-32" data-testid="hero-section">
                <div className="blueprint-pattern"></div>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="text-center">
                        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-[#0A0A0A] mb-6">
                            AI-Powered ECSA Professional<br />Engineer Assessment
                        </h1>
                        <p className="text-base sm:text-lg text-[#666666] max-w-3xl mx-auto mb-8 leading-relaxed">
                            Evaluate your readiness for professional engineer registration against the 11 ECSA outcomes.
                            Get detailed assessments, connect with mentors, and export reports for your ECSA application.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            {!user && (
                                <>
                                    <Button
                                        size="lg"
                                        onClick={() => openAuth('register')}
                                        data-testid="hero-get-started-btn"
                                        className="bg-[#002FA7] hover:bg-[#0040D9] text-white rounded-sm px-8 py-6 text-base font-medium"
                                    >
                                        Start Your Assessment
                                    </Button>
                                    <Button
                                        size="lg"
                                        variant="outline"
                                        onClick={() => openAuth('register')}
                                        data-testid="hero-become-mentor-btn"
                                        className="border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7]/5 rounded-sm px-8 py-6 text-base font-medium"
                                    >
                                        Become a Mentor
                                    </Button>
                                </>
                            )}
                            {user && (
                                <Button
                                    size="lg"
                                    onClick={() => window.location.href = '/dashboard'}
                                    data-testid="hero-go-to-dashboard-btn"
                                    className="bg-[#002FA7] hover:bg-[#0040D9] text-white rounded-sm px-8 py-6 text-base font-medium"
                                >
                                    Go to Dashboard
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section className="py-20 bg-white" data-testid="features-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#666666] mb-3">FEATURES</p>
                        <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-[#0A0A0A]">
                            Comprehensive Professional Development Platform
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        <Card className="swiss-card p-6" data-testid="feature-card-ai-assessment">
                            <div className="w-12 h-12 bg-[#002FA7]/10 rounded-sm flex items-center justify-center mb-4">
                                <ChartBar size={24} weight="duotone" className="text-[#002FA7]" />
                            </div>
                            <h3 className="font-heading text-xl font-medium mb-2 text-[#0A0A0A]">AI Assessment</h3>
                            <p className="text-sm text-[#666666] leading-relaxed">
                                Get evaluated against all 11 ECSA outcomes using GPT-5.2, Claude Sonnet 4.5, or Gemini 3 Pro.
                            </p>
                        </Card>
                        <Card className="swiss-card p-6" data-testid="feature-card-mentorship">
                            <div className="w-12 h-12 bg-[#FF5A00]/10 rounded-sm flex items-center justify-center mb-4">
                                <Users size={24} weight="duotone" className="text-[#FF5A00]" />
                            </div>
                            <h3 className="font-heading text-xl font-medium mb-2 text-[#0A0A0A]">Mentorship Matching</h3>
                            <p className="text-sm text-[#666666] leading-relaxed">
                                Connect with experienced mentors in your discipline through auto-matching or manual search.
                            </p>
                        </Card>
                        <Card className="swiss-card p-6" data-testid="feature-card-reports">
                            <div className="w-12 h-12 bg-[#009944]/10 rounded-sm flex items-center justify-center mb-4">
                                <FileText size={24} weight="duotone" className="text-[#009944]" />
                            </div>
                            <h3 className="font-heading text-xl font-medium mb-2 text-[#0A0A0A]">Export Reports</h3>
                            <p className="text-sm text-[#666666] leading-relaxed">
                                Download professional PDF and Word reports ready for your ECSA application submission.
                            </p>
                        </Card>
                        <Card className="swiss-card p-6" data-testid="feature-card-tracking">
                            <div className="w-12 h-12 bg-[#FFB800]/10 rounded-sm flex items-center justify-center mb-4">
                                <Briefcase size={24} weight="duotone" className="text-[#FFB800]" />
                            </div>
                            <h3 className="font-heading text-xl font-medium mb-2 text-[#0A0A0A]">Progress Tracking</h3>
                            <p className="text-sm text-[#666666] leading-relaxed">
                                Track your development across multiple assessments and identify areas for improvement.
                            </p>
                        </Card>
                    </div>
                </div>
            </section>

            {/* How It Works */}
            <section className="py-20 bg-[#F9F9F6]" data-testid="how-it-works-section">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#666666] mb-3">PROCESS</p>
                        <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-[#0A0A0A]">
                            How It Works
                        </h2>
                    </div>
                    <div className="space-y-8">
                        <div className="flex gap-6" data-testid="step-1">
                            <div className="flex-shrink-0 w-12 h-12 bg-[#002FA7] text-white rounded-sm flex items-center justify-center font-mono font-bold text-lg">
                                1
                            </div>
                            <div>
                                <h3 className="font-heading text-lg font-medium mb-2 text-[#0A0A0A]">Submit Your Work Experience</h3>
                                <p className="text-sm text-[#666666] leading-relaxed">
                                    Document your engineering work experience, upload supporting documents, and describe your responsibilities and achievements.
                                </p>
                            </div>
                        </div>
                        <div className="flex gap-6" data-testid="step-2">
                            <div className="flex-shrink-0 w-12 h-12 bg-[#002FA7] text-white rounded-sm flex items-center justify-center font-mono font-bold text-lg">
                                2
                            </div>
                            <div>
                                <h3 className="font-heading text-lg font-medium mb-2 text-[#0A0A0A]">Get AI Assessment</h3>
                                <p className="text-sm text-[#666666] leading-relaxed">
                                    Choose your preferred AI model to evaluate your experience against all 11 ECSA outcomes with detailed scoring and recommendations.
                                </p>
                            </div>
                        </div>
                        <div className="flex gap-6" data-testid="step-3">
                            <div className="flex-shrink-0 w-12 h-12 bg-[#002FA7] text-white rounded-sm flex items-center justify-center font-mono font-bold text-lg">
                                3
                            </div>
                            <div>
                                <h3 className="font-heading text-lg font-medium mb-2 text-[#0A0A0A]">Connect with Mentors</h3>
                                <p className="text-sm text-[#666666] leading-relaxed">
                                    Browse experienced mentors in your discipline or let our algorithm match you with the best fit for your development needs.
                                </p>
                            </div>
                        </div>
                        <div className="flex gap-6" data-testid="step-4">
                            <div className="flex-shrink-0 w-12 h-12 bg-[#002FA7] text-white rounded-sm flex items-center justify-center font-mono font-bold text-lg">
                                4
                            </div>
                            <div>
                                <h3 className="font-heading text-lg font-medium mb-2 text-[#0A0A0A]">Export & Submit</h3>
                                <p className="text-sm text-[#666666] leading-relaxed">
                                    Download your assessment reports in PDF or Word format, ready for submission to ECSA when you're ready to apply.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 bg-[#002FA7]" data-testid="cta-section">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-white mb-6">
                        Ready to Advance Your Engineering Career?
                    </h2>
                    <p className="text-base text-white/90 mb-8 leading-relaxed">
                        Join South African engineers taking the next step toward professional registration.
                    </p>
                    {!user ? (
                        <Button
                            size="lg"
                            onClick={() => openAuth('register')}
                            data-testid="cta-get-started-btn"
                            className="bg-white text-[#002FA7] hover:bg-[#F9F9F6] rounded-sm px-8 py-6 text-base font-medium"
                        >
                            Start Your Assessment Now
                        </Button>
                    ) : (
                        <Button
                            size="lg"
                            onClick={() => window.location.href = '/dashboard'}
                            data-testid="cta-dashboard-btn"
                            className="bg-white text-[#002FA7] hover:bg-[#F9F9F6] rounded-sm px-8 py-6 text-base font-medium"
                        >
                            Go to Your Dashboard
                        </Button>
                    )}
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-white border-t border-[#DCDCDA] py-8" data-testid="footer">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center text-sm text-[#666666]">
                        <p>© 2026 MyProfessionalPath Platform. Supporting South African engineers in their professional development.</p>
                    </div>
                </div>
            </footer>

            {/* Auth Modal */}
            {showAuth && (
                <AuthModal
                    mode={authMode}
                    onClose={() => setShowAuth(false)}
                    onSwitchMode={(mode) => setAuthMode(mode)}
                />
            )}
        </div>
    );
};

export default LandingPage;